import { Component } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { getTranslationProvider } from '../core/i18n/getTranslationProvider';
import { TranslatableComponent } from '../core/i18n/TranslatableComponent';

@Component({
  imports: [TranslocoModule],
  template: `
    <ng-container *transloco="let t; prefix: scope">
      <main class="min-h-screen bg-blg-grey-50 px-4 py-8 sm:px-6 lg:px-8">
        <div class="mx-auto flex min-h-[calc(100vh-4rem)] max-w-6xl items-center">
          <section
            aria-labelledby="missing-firms-title"
            class="grid w-full overflow-hidden rounded-lg border border-bl-grey-200 bg-white shadow-sm lg:grid-cols-2">
            <div class="p-6 sm:p-10 lg:p-12">
              <p
                class="mb-5 inline-flex rounded-full bg-bl-grey-50 px-3 py-1 text-sm font-semibold text-bl-blue-light-600">
                {{ t('status') }}
              </p>
              <h1 id="missing-firms-title" class="text-headline mb-4 font-bold text-bl-grey-700">
                {{ t('title') }}
              </h1>
              <p class="text-sub-headline mb-8 text-bl-grey-700">
                {{ t('description') }}
              </p>

              <div class="space-y-5 border-t border-bl-grey-200 pt-6">
                <div class="flex items-start gap-3">
                  <i class="pi pi-phone mt-1 text-bl-blue-light-600" aria-hidden="true"></i>
                  <div class="flex flex-col">
                    <span class="text-sm text-bl-grey-500">{{ t('phoneLabel') }}</span>
                    <a class="font-semibold text-bl-blue-light-600 hover:underline" href="tel:+46650541400">
                      0650-54 14 00
                    </a>
                  </div>
                </div>

                <div class="flex items-start gap-3">
                  <i class="pi pi-envelope mt-1 text-bl-blue-light-600" aria-hidden="true"></i>
                  <div class="flex flex-col">
                    <span class="text-sm text-bl-grey-500">{{ t('emailLabel') }}</span>
                    <a
                      class="break-all font-semibold text-bl-blue-light-600 hover:underline"
                      href="mailto:salj.se@bjornlunden.com">
                      salj.se&#64;bjornlunden.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div class="border-t border-bl-grey-200 bg-bl-grey-50 p-6 sm:p-10 lg:border-l lg:border-t-0">
              <h2 class="text-sub-headline mb-2 font-bold text-bl-grey-700">{{ t('formTitle') }}</h2>
              <p class="mb-6 text-description text-bl-grey-600">{{ t('formDescription') }}</p>

              <form class="space-y-4" (submit)="showMockConfirmation($event)">
                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium text-bl-grey-700" for="contact-name">{{ t('nameLabel') }}</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    autocomplete="name"
                    required
                    class="w-full rounded border border-bl-grey-200 bg-white px-3 py-2 text-bl-grey-700 outline-none focus:border-bl-blue-light-600 focus:ring-1 focus:ring-bl-blue-light-600" />
                </div>

                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium text-bl-grey-700" for="contact-email">{{
                    t('yourEmailLabel')
                  }}</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    autocomplete="email"
                    required
                    class="w-full rounded border border-bl-grey-200 bg-white px-3 py-2 text-bl-grey-700 outline-none focus:border-bl-blue-light-600 focus:ring-1 focus:ring-bl-blue-light-600" />
                </div>

                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium text-bl-grey-700" for="contact-firm">{{ t('firmLabel') }}</label>
                  <input
                    id="contact-firm"
                    name="firm"
                    type="text"
                    autocomplete="organization"
                    class="w-full rounded border border-bl-grey-200 bg-white px-3 py-2 text-bl-grey-700 outline-none focus:border-bl-blue-light-600 focus:ring-1 focus:ring-bl-blue-light-600" />
                </div>

                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium text-bl-grey-700" for="contact-message">{{
                    t('messageLabel')
                  }}</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows="4"
                    maxlength="1000"
                    required
                    class="w-full resize-y rounded border border-bl-grey-200 bg-white px-3 py-2 text-bl-grey-700 outline-none focus:border-bl-blue-light-600 focus:ring-1 focus:ring-bl-blue-light-600"></textarea>
                </div>

                <button
                  type="submit"
                  class="inline-flex items-center gap-2 rounded bg-bl-blue-light-600 px-4 py-2.5 font-semibold text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-bl-blue-light-600 focus:ring-offset-2">
                  {{ t('sendButton') }}
                </button>
              </form>

              @if (mockSubmitted) {
                <p class="mt-4 rounded border border-bl-grey-200 bg-white p-3 text-sm text-bl-grey-700" role="status">
                  {{ t('mockConfirmation') }}
                </p>
              } @else {
                <p class="mt-4 text-sm text-bl-grey-500">{{ t('mockNotice') }}</p>
              }
            </div>
          </section>
        </div>
      </main>
    </ng-container>
  `,
  styles: `
    :host {
      display: block;
      min-height: 100%;
    }
  `,
  providers: [getTranslationProvider('MissingFirmsComponent')],
  standalone: true,
})
export class MissingFirmsComponent extends TranslatableComponent {
  public mockSubmitted = false;

  public showMockConfirmation(event: Event): void {
    event.preventDefault();
    this.mockSubmitted = true;
  }
}
